import React, { useMemo, useState } from 'react';
import useSWR, { mutate as mutateGlobal } from 'swr';
import axios from 'axios';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';
import LanggananDialog from './LanggananDialog';
import { HARI_PERINGATAN, formatTanggal, getApiBaseUrl, labelStatus } from './langgananUtil';

// Dashboard superadmin: cabang mana yang sudah expired atau segera berakhir (H-30) supaya bisa ditagih/diperpanjang,
// plus hitungan semua status. Data dari GET /cabang (sudah memuat status langganan) dan GET /paket.
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);

const RingkasanLanggananSuperadmin = () => {
  const base = getApiBaseUrl();
  const { data: cabangRes } = useSWR(`${base}/cabang`, ambil);
  const { data: paketRes } = useSWR(`${base}/paket`, ambil);
  const [diperpanjang, setDiperpanjang] = useState(null);

  const cabangList = Array.isArray(cabangRes?.data) ? cabangRes.data : [];
  const pakets = Array.isArray(paketRes?.data) ? paketRes.data : [];

  const { hitung, perhatian } = useMemo(() => {
    const h = { expired: 0, segera: 0, aktif: 0, permanen: 0 };
    const p = [];
    cabangList.forEach((c) => {
      const lg = c.langganan;
      if (!lg) return;
      if (lg.permanen) h.permanen += 1;
      else if (lg.expired) { h.expired += 1; p.push(c); }
      else if (lg.sisaHari <= HARI_PERINGATAN) { h.segera += 1; p.push(c); }
      else h.aktif += 1;
    });
    // Yang paling mendesak dulu: expired paling lama, lalu yang paling cepat berakhir.
    p.sort((a, b) => a.langganan.sisaHari - b.langganan.sisaHari);
    return { hitung: h, perhatian: p };
  }, [cabangList]);

  if (!cabangRes) return null;

  const namaPaket = (uuid) => pakets.find((x) => x.uuid === uuid)?.nama || '-';
  const kartu = [
    ['Expired', hitung.expired, 'error.main'],
    [`Segera berakhir (≤ ${HARI_PERINGATAN} hari)`, hitung.segera, 'warning.dark'],
    ['Aktif', hitung.aktif, 'success.main'],
    ['Permanen', hitung.permanen, 'text.primary'],
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Langganan cabang</Typography>
        <Link component={RouterLink} to="/branch" underline="hover" variant="body2">Kelola semua cabang</Link>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {kartu.map(([judul, nilai, warna]) => (
          <Card key={judul} variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">{judul}</Typography>
              <Typography variant="h5" fontWeight={700} color={warna}>{nilai}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {perhatian.length === 0 ? (
        <Alert severity="success">Semua cabang aman: tidak ada yang expired atau akan berakhir dalam {HARI_PERINGATAN} hari.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cabang</TableCell>
                <TableCell>Paket</TableCell>
                <TableCell>Berakhir</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {perhatian.map((c) => {
                const st = labelStatus(c.langganan);
                const lewat = c.langganan.expired ? Math.abs(c.langganan.sisaHari) : 0;
                return (
                  <TableRow key={c.uuid} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{c.namacabang}</Typography>
                      <Typography variant="caption" color="text.secondary">{c.tipe_cabang}</Typography>
                    </TableCell>
                    <TableCell>{namaPaket(c.paketUuid)}</TableCell>
                    <TableCell>
                      {formatTanggal(c.masaAktifSampai)}
                      {c.langganan.expired && lewat > 0 && (
                        <Typography variant="caption" color="error" display="block">lewat {lewat} hari</Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" color={st.color} label={st.text} /></TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => setDiperpanjang(c)}>Perpanjang</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <LanggananDialog
        open={Boolean(diperpanjang)}
        cabang={diperpanjang}
        pakets={pakets}
        onClose={() => setDiperpanjang(null)}
        onSaved={() => mutateGlobal(`${base}/cabang`)}
      />
    </Box>
  );
};

export default RingkasanLanggananSuperadmin;
